/**
 * Does something else own this Esc? A page's own Esc shortcut (a form's
 * Cancel) stands aside while an overlay is OPEN on top of it — the overlay's
 * Esc closes the overlay, and must not also cancel the form beneath — and
 * never otherwise.
 *
 * "Open" is the point. antd keeps a modal MOUNTED once it has been opened,
 * hidden by `display: none` on its wrap, so asking the DOM whether any
 * `.ant-modal` exists answers yes for the rest of the page's life after the
 * first modal closes. That is how the new-item form's Esc went dead (the
 * owner's live pass): its own Keep-as-draft prompt, closed once with Esc or
 * ✕, or the keyboard shortcuts list opened once from the shell, and every
 * later Esc on the form found the hidden modal and did nothing. A modal
 * counts here only while it is shown. The popups antd hides with a class
 * (select, picker, dropdown, popover — Popconfirm is a popover) are open
 * while they lack it.
 *
 * Also stood aside for: an Esc something already handled
 * (`defaultPrevented`), one that ends an IME composition, and one pressed
 * inside an overlay (whose own handler has the say).
 */

const OPEN_POPUPS = [
	'.ant-select-open',
	'.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
	'.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
	'.ant-dropdown:not(.ant-dropdown-hidden)',
	'.ant-popover:not(.ant-popover-hidden)',
].join(', ');

const OVERLAY_ROOTS = '.ant-modal-wrap, .ant-drawer, .ant-popover, .ant-dropdown, .ant-select-dropdown, .ant-picker-dropdown';

function isDisplayed(element) {
	const view = element?.ownerDocument?.defaultView;
	for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
		if (node.style?.display === 'none') return false;
		if (view && view.getComputedStyle(node).display === 'none') return false;
	}
	return true;
}

/** Is a modal or drawer shown right now (not merely mounted)? */
export function hasOpenOverlay(doc = document) {
	if (!doc) return false;
	const layers = doc.querySelectorAll('.ant-modal-wrap, .ant-drawer-open');
	for (const layer of layers) {
		if (isDisplayed(layer)) return true;
	}
	return Boolean(doc.querySelector(OPEN_POPUPS));
}

/** True when a page's own Esc shortcut should leave this keydown alone. */
export function escapeBelongsElsewhere(event, doc = document) {
	if (!event) return true;
	if (event.defaultPrevented) return true;
	if (event.isComposing || event.keyCode === 229) return true;
	const target = event.target;
	if (target && typeof target.closest === 'function' && target.closest(OVERLAY_ROOTS)) return true;
	return hasOpenOverlay(doc);
}

export default escapeBelongsElsewhere;
